import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react"
import { MarkdownField, type MarkdownFieldHandle } from "./MarkdownField"
import { api, type CommentAttachment } from "../lib/api"
import { useLoad } from "./ui"
import { attachmentRef, resolveAttachmentHref, type AttachmentRef } from "../lib/attachment-refs"
import { sizeProblem } from "../lib/fileTypes"

type CommentEditorProps = {
  initialBody?: string
  initialAttachments?: CommentAttachment[]
  onSubmit: (body: string, removeAttachmentIds: string[], attachmentIds: string[]) => Promise<void>
  onCancel?: () => void
  placeholder?: string
  compact?: boolean
}

const MAX_FILES = 10

async function uploadDraft(file: File) {
  const form = new FormData()
  form.append("file", file)
  return api.postMultipart<{ id: string; url: string }>("/projects/attachments", form)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function CommentEditor({
  initialBody = "",
  initialAttachments = [],
  onSubmit,
  onCancel,
  placeholder = "Write a comment...",
  compact = false,
}: CommentEditorProps) {
  const [body, setBody] = useState(initialBody)
  const [files, setFiles] = useState<File[]>([])
  const [removeIds, setRemoveIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const field = useRef<MarkdownFieldHandle | null>(null)

  // The editor reads its value once, at mount. Bumping this is how the box
  // clears itself after a successful post — there is no imperative setter.
  const [editorKey, setEditorKey] = useState(0)

  // Images uploaded from inside the editor. They are already in the bucket as
  // drafts; posting the comment is what claims them. Their ids are also what
  // the body's `attachment:` refs point at, which is why the list has to
  // survive until submit rather than living in the editor.
  const [drafts, setDrafts] = useState<AttachmentRef[]>([])

  // Picked files go up the same way, one request each, at submit. Sent inline
  // with the comment, ten of them shared one request body and its cap; a
  // handful of large files overran it before the route could say why. Kept
  // across a failed submit so a retry does not upload the same file twice.
  const uploaded = useRef(new Map<File, string>())

  // Everything an `attachment:` ref in this body could name: what the comment
  // already had, plus what this session uploaded.
  const refs = useMemo<AttachmentRef[]>(
    () => [...initialAttachments, ...drafts],
    [initialAttachments, drafts],
  )

  // Read through a ref: Crepe captures proxyDomURL once per image node view,
  // and a node mounted right after an upload has to resolve the draft that
  // upload just created.
  const refsRef = useRef(refs)
  useEffect(() => {
    refsRef.current = refs
  }, [refs])

  const uploadInline = useCallback(async (file: File) => {
    const draft = await uploadDraft(file)
    // Synchronously as well as through state: Crepe resolves the new image's
    // src as soon as this returns, before the effect that syncs refsRef runs.
    refsRef.current = [...refsRef.current, draft]
    setDrafts((current) => [...current, draft])
    // The document gets the ref, never the signed URL: remark escapes the `&`
    // in a query string, and the URL expires in an hour. proxyDomURL below is
    // what makes the image visible while writing.
    return attachmentRef(draft.id)
  }, [])

  const proxyDomURL = useCallback((url: string) => {
    return resolveAttachmentHref(refsRef.current)(url) ?? url
  }, [])

  // Everyone who can act in this company, for the `@` menu. Fetched once and
  // filtered here: a company roster is small, and a request per keystroke is
  // not. The editor mounts on the first click, so a thread nobody writes in
  // never asks for it.
  const people = useLoad(() =>
    api
      .get<{ employees: Array<{ id: string; name: string }> }>("/auth/employees/roster")
      .catch(() => ({ employees: [] })),
  )
  const roster = people.data?.employees ?? []
  const rosterRef = useRef(roster)
  useEffect(() => {
    rosterRef.current = roster
  }, [roster])

  const searchMentions = useCallback(async (query: string) => {
    const needle = query.trim().toLowerCase()
    const all = rosterRef.current
    if (!needle) return all.slice(0, 8)
    return all
      .filter((person) => person.name.toLowerCase().includes(needle))
      // A name that starts with the query is the one being typed towards.
      .sort((a, b) => {
        const rank = (n: string) => (n.toLowerCase().startsWith(needle) ? 0 : 1)
        return rank(a.name) - rank(b.name) || a.name.localeCompare(b.name)
      })
      .slice(0, 8)
  }, [])

  const existingAttachments = initialAttachments.filter((a) => !removeIds.includes(a.id))

  // One object URL per picked image, revoked when the selection changes or the
  // editor unmounts. Created inline in the JSX it minted a fresh URL on every
  // render and freed none of them.
  const previews = useMemo(
    () => files.map((file) => (file.type.startsWith("image/") ? URL.createObjectURL(file) : null)),
    [files],
  )
  useEffect(
    () => () => {
      for (const url of previews) if (url) URL.revokeObjectURL(url)
    },
    [previews],
  )

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Read the field directly rather than the debounced onChange, so a submit
    // straight after a keystroke is not a quarter-second stale. What it holds
    // is already what gets stored — the editor's document carries refs, not
    // URLs, so there is nothing to rewrite here.
    const stored = field.current?.getValue() ?? body

    if (!stored.trim() && files.length === 0 && existingAttachments.length === 0) {
      setError("Comment cannot be empty")
      return
    }

    // Validate files
    for (const file of files) {
      const problem = sizeProblem(file)
      if (problem) {
        setError(problem)
        return
      }
    }

    if (existingAttachments.length + files.length > MAX_FILES) {
      setError(`Cannot attach more than ${MAX_FILES} files`)
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const picked: string[] = []
      for (const file of files) {
        let id = uploaded.current.get(file)
        if (!id) {
          id = (await uploadDraft(file)).id
          uploaded.current.set(file, id)
        }
        picked.push(id)
      }
      await onSubmit(stored, removeIds, [...drafts.map((draft) => draft.id), ...picked])
      uploaded.current.clear()
      // Reset form after successful submit
      setBody("")
      setFiles([])
      setRemoveIds([])
      setDrafts([])
      setEditorKey((n) => n + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit comment")
    } finally {
      setSubmitting(false)
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files || [])
    addFiles(selected)
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function addFiles(newFiles: File[]) {
    const combined = [...files, ...newFiles]
    if (existingAttachments.length + combined.length > MAX_FILES) {
      setError(`Cannot attach more than ${MAX_FILES} files`)
      return
    }
    setFiles(combined)
    setError(null)
  }

  function removeFile(index: number) {
    setFiles(files.filter((_, i) => i !== index))
  }

  function removeExistingAttachment(id: string) {
    setRemoveIds([...removeIds, id])
  }

  function restoreExistingAttachment(id: string) {
    setRemoveIds(removeIds.filter((rid) => rid !== id))
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault()
    setDragOver(true)
  }

  function handleDragLeave(e: DragEvent) {
    e.preventDefault()
    setDragOver(false)
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const dropped = Array.from(e.dataTransfer.files)
    addFiles(dropped)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      {/* Editor area. The drop handlers sit here and stop the event before
          ProseMirror sees it: a file dropped on a comment is an attachment,
          not something to insert into the document. */}
      <div
        className={`relative rounded-lg transition-colors ${
          dragOver ? "ring-2 ring-primary" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <MarkdownField
          key={editorKey}
          initialValue={body}
          handle={field}
          onChange={setBody}
          onUpload={uploadInline}
          proxyDomURL={proxyDomURL}
          onMentionSearch={searchMentions}
          placeholder={placeholder}
          compact
          // A new-comment box starts as a placeholder and mounts the editor on
          // the first click, so a task page with several threads loads none of
          // it. Editing an existing comment already *was* a click, so it opens
          // straight into the editor.
          startEditing={compact}
          ariaLabel="Comment"
        />

        {/* Drag overlay */}
        {dragOver && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-primary/10 text-sm font-medium text-primary">
            Drop files here
          </div>
        )}
      </div>

      {/* Attachments section */}
      <div className="flex flex-col gap-2">
        {/* Existing attachments */}
        {initialAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {initialAttachments.map((att) => {
              const isRemoved = removeIds.includes(att.id)
              return (
                <div
                  key={att.id}
                  className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${
                    isRemoved
                      ? "border-destructive/50 bg-destructive/10 opacity-50"
                      : "border-border bg-muted"
                  }`}
                >
                  {att.inline && att.mimeType.startsWith("image/") && att.url && (
                    <img
                      src={att.url}
                      alt={att.filename}
                      className="h-8 w-8 rounded object-cover"
                    />
                  )}
                  <span className="max-w-[150px] truncate">{att.filename}</span>
                  <span className="text-muted-foreground">{formatFileSize(att.sizeBytes)}</span>
                  <button
                    type="button"
                    onClick={() =>
                      isRemoved ? restoreExistingAttachment(att.id) : removeExistingAttachment(att.id)
                    }
                    className="text-muted-foreground hover:text-foreground"
                    title={isRemoved ? "Restore" : "Remove"}
                  >
                    {isRemoved ? "↩" : "×"}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {/* New files */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((file, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1 text-xs"
              >
                {previews[i] && (
                  <img
                    src={previews[i]!}
                    alt={file.name}
                    className="h-8 w-8 rounded object-cover"
                  />
                )}
                <span className="max-w-[150px] truncate">{file.name}</span>
                <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-muted-foreground hover:text-foreground"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Attach files"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            Attach
          </button>
        </div>

        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-md border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Posting..." : "Post"}
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </form>
  )
}
